import { collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { validateRutUY } from "./validateRutUY";
import { tolerantId } from "./firestoreHelpers";
import { bidCanonico } from "./consultasEdificio";

/**
 * Alta rápida de proveedor desde otras pantallas (ej. "Nuevo Gasto").
 * Mismo modelo que ProveedoresEdificio: doc en `supplier` con `supplierId`
 * secuencial (primer entero libre) + vínculo en `supplierbuilding`.
 *
 * Si el RUT ya existe en el sistema NO crea un duplicado: reutiliza ese
 * proveedor y solo lo vincula al edificio (si no lo estaba).
 *
 * La ficha nueva lleva createdByBid (el edificio en forma canónica, "10"):
 * con las reglas 0D solo el staff de ese edificio la edita después.
 *
 * @returns {Promise<{supplierId: string, name: string, reused: boolean}>}
 */
export async function createSupplierForBuilding({ name, rut = "", type = "", telephone = "", email = "" }, buildingId) {
  const cleanName = String(name || "").trim();
  if (!cleanName) throw new Error("El nombre del proveedor es obligatorio.");
  const cleanRut = String(rut || "").replace(/\D/g, "");
  if (cleanRut) {
    const v = validateRutUY(cleanRut);
    if (!v.valid) throw new Error(v.message || "RUT inválido.");
  }
  if (buildingId == null || String(buildingId).trim() === "") {
    // Sin edificio no hay vínculo posible (ni createdByBid): mejor avisar antes de escribir.
    throw new Error("Elegí un edificio para dar de alta el proveedor.");
  }
  const bid = Number(buildingId);

  const linkToBuilding = async (supplierId) => {
    // El vínculo puede haberse guardado con el edificio en número o string.
    const linkSnap = await getDocs(query(
      collection(db, "supplierbuilding"),
      where("supplierId", "==", String(supplierId)),
      where("buildingId", "in", tolerantId(bid)),
    ));
    if (linkSnap.empty) {
      await addDoc(collection(db, "supplierbuilding"), {
        supplierId: String(supplierId), buildingId: bid, showToClient: true, createdAt: serverTimestamp(),
      });
    }
  };

  // RUT ya cargado → reutilizar (evita proveedores duplicados).
  if (cleanRut) {
    const dup = await getDocs(query(collection(db, "supplier"), where("rut", "==", cleanRut)));
    if (!dup.empty) {
      const existing = dup.docs[0].data();
      await linkToBuilding(existing.supplierId);
      return { supplierId: String(existing.supplierId), name: existing.name || cleanName, reused: true };
    }
  }

  // Próximo supplierId libre (mismo criterio que ProveedoresEdificio).
  const all = await getDocs(collection(db, "supplier"));
  const used = new Set(all.docs.map((d) => String(d.data().supplierId || d.data().supplierid)));
  let candidate = 1;
  while (used.has(String(candidate))) candidate += 1;
  let supplierId = String(candidate);
  // Por si otra persona creó uno en simultáneo.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const clash = await getDocs(query(collection(db, "supplier"), where("supplierId", "==", supplierId)));
    if (clash.empty) break;
    supplierId = String(Number(supplierId) + 1);
  }

  await addDoc(collection(db, "supplier"), {
    name: cleanName, rut: cleanRut, type: String(type || "").trim(), telephone: String(telephone || "").trim(),
    email: String(email || "").trim(), address: "", razonSocial: "", location: null,
    supplierId, createdByBid: bidCanonico(buildingId), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  await linkToBuilding(supplierId);
  return { supplierId, name: cleanName, reused: false };
}
